import { useCallback, useEffect, useMemo, useRef, useState } from "react"

interface CountDownActions {
  start: (ttc?: number) => void
  pause: () => void
  resume: () => void
  reset: () => void
}

const useCountDown = (timeToCount = 60 * 1000, interval = 1000): [number, CountDownActions] => {
  const [timeLeft, setTimeLeft] = useState(0)
  const timer = useRef<{
    started: number
    lastInterval: number
    timeToCount: number
    timeLeft: number
    requestId?: number
  }>({
    started: 0,
    lastInterval: 0,
    timeToCount: 0,
    timeLeft: 0,
  })

  const run = useCallback(
    (ts: number) => {
      if (!timer.current.started) {
        timer.current.started = ts
        timer.current.lastInterval = ts
      }

      const localInterval = Math.min(interval, timer.current.timeLeft || Infinity)
      if (ts - timer.current.lastInterval >= localInterval) {
        timer.current.lastInterval += localInterval
        setTimeLeft(currentTimeLeft => {
          timer.current.timeLeft = currentTimeLeft - localInterval
          return timer.current.timeLeft
        })
      }

      if (ts - timer.current.started < timer.current.timeToCount) {
        timer.current.requestId = window.requestAnimationFrame(run)
      } else {
        timer.current = {
          started: 0,
          lastInterval: 0,
          timeToCount: 0,
          timeLeft: 0,
        }
        setTimeLeft(0)
      }
    },
    [interval],
  )

  const start = useCallback(
    (ttc?: number) => {
      if (timer.current.requestId) {
        window.cancelAnimationFrame(timer.current.requestId)
      }

      const newTimeToCount = ttc !== undefined ? ttc : timeToCount
      timer.current = {
        started: 0,
        lastInterval: 0,
        timeToCount: newTimeToCount,
        timeLeft: newTimeToCount,
      }
      timer.current.requestId = window.requestAnimationFrame(run)

      setTimeLeft(newTimeToCount)
    },
    [timeToCount, run],
  )

  const pause = useCallback(() => {
    if (timer.current.requestId) {
      window.cancelAnimationFrame(timer.current.requestId)
    }
    timer.current.started = 0
    timer.current.lastInterval = 0
    timer.current.timeToCount = timer.current.timeLeft
  }, [])

  const resume = useCallback(() => {
    if (!timer.current.started && timer.current.timeLeft > 0) {
      if (timer.current.requestId) {
        window.cancelAnimationFrame(timer.current.requestId)
      }
      timer.current.requestId = window.requestAnimationFrame(run)
    }
  }, [run])

  const reset = useCallback(() => {
    if (timer.current.timeLeft) {
      if (timer.current.requestId) {
        window.cancelAnimationFrame(timer.current.requestId)
      }
      timer.current = {
        started: 0,
        lastInterval: 0,
        timeToCount: 0,
        timeLeft: 0,
      }
      setTimeLeft(0)
    }
  }, [])

  const actions = useMemo(() => ({ start, pause, resume, reset }), [start, pause, resume, reset])

  useEffect(() => reset, [reset])

  return [timeLeft, actions]
}

export default useCountDown
